import { useEffect, useRef, useState } from 'react';
import { loadYoutubeIframeApi, YTPlayer } from 'utils/loadYoutubeIframeApi';
import { LIVE_STREAM_VIDEO_ID } from 'utils/constants';
import { useYoutubeVideoDetails } from 'hooks/useYoutubeVideoDetails';
import './liveVideoSection.css';

const PLAYER_ELEMENT_ID = 'live-video-player';
const MUTE_POLL_INTERVAL_MS = 500;

interface Props {
  videoId?: string;
  onDismiss?: () => void;
}

const LiveVideoSection = ({
  videoId = LIVE_STREAM_VIDEO_ID,
  onDismiss,
}: Props) => {
  const playerRef = useRef<YTPlayer | null>(null);
  const { title, description, viewers } = useYoutubeVideoDetails(videoId);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let mutePollId: ReturnType<typeof setInterval> | undefined;

    loadYoutubeIframeApi()
      .then((YT) => {
        if (cancelled) return;

        playerRef.current = new YT.Player(PLAYER_ELEMENT_ID, {
          videoId,
          playerVars: {
            autoplay: 1,
            mute: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady: (event) => {
              setMuted(event.target.isMuted());

              // The IFrame API has no mute/volume-change event, so poll to
              // pick up changes made via YouTube's own UI controls.
              mutePollId = setInterval(() => {
                setMuted((prev) => {
                  const isMuted = event.target.isMuted();
                  return isMuted === prev ? prev : isMuted;
                });
              }, MUTE_POLL_INTERVAL_MS);
            },
            onError: (event) => {
              console.error(
                `LiveVideoSection: YouTube player error code ${event.data}`
              );
            },
          },
        });
      })
      .catch((error) => {
        console.error('LiveVideoSection: failed to load YouTube player', error);
      });

    return () => {
      cancelled = true;
      if (mutePollId) clearInterval(mutePollId);
      playerRef.current?.destroy();
    };
  }, [videoId]);

  const toggleMuted = () => {
    const player = playerRef.current;
    if (!player) return;

    if (muted) {
      player.unMute();
      player.setVolume(100);
    } else {
      player.mute();
    }
    setMuted(!muted);
  };

  return (
    <section className="live-video-section">
      <div className="live-video-embed container">
        {title && <h2 className="live-video-title">{title}</h2>}
        <div className="live-video-toolbar">
          <div className="live-video-toolbar-right">
            <button
              type="button"
              className="live-video-mute-toggle"
              onClick={toggleMuted}
              aria-label={muted ? 'Unmute live video' : 'Mute live video'}
            >
              {muted ? 'Unmute' : 'Mute'}
            </button>
            {viewers !== null && (
              <div className="live-video-viewers-badge">
                <span className="live-video-viewers-dot" />
                {viewers.toLocaleString()} watching
              </div>
            )}
            {onDismiss && (
              <button
                type="button"
                className="live-video-dismiss"
                onClick={onDismiss}
                aria-label="Dismiss live video"
              >
                &times;
              </button>
            )}
          </div>
        </div>
        <div className="responsive-video-wrapper">
          <div id={PLAYER_ELEMENT_ID} />
        </div>
        {description && <p className="live-video-description">{description}</p>}
      </div>
    </section>
  );
};

export default LiveVideoSection;
